import React, { useEffect, useState } from "react";
import { Navigate } from "react-router-dom";
import { Container } from "@/components/ui-kit";
import { fetchContacts } from "@/lib/api";
import { Mail, Phone, Globe, RefreshCw } from "lucide-react";
import { useAdminAuth } from "@/hooks/useAdminAuth";

export default function AdminContacts() {
  const { token, checking, isLoggedIn, revealAdminUi } = useAdminAuth();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  const load = () => {
    if (!token) return;
    setLoading(true);
    setError("");
    fetchContacts(token)
      .then(setItems)
      .catch(() => setError("Could not load submissions."))
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  if (checking) return null;
  if (!revealAdminUi) return <Navigate to="/admin/login" replace />;
  if (!isLoggedIn) return <div className="min-h-screen bg-slate-50 px-4 py-16 text-center text-slate-500">DEBUG — Admin UI revealed, but this page needs a real login to load real data.</div>;

  return (
    <div data-testid="admin-contacts-page" className="bg-[#F8FAFC] min-h-[70vh]">
      <Container className="py-12">
        <div className="flex items-center justify-between mb-8">
          <div>
            <h1 className="text-2xl font-bold font-display text-[#0F172A]">Contact Submissions</h1>
            <p className="text-sm text-[#475569] mt-1">{items.length} total</p>
          </div>
          <button
            data-testid="admin-refresh"
            onClick={load}
            className="inline-flex items-center gap-2 rounded-xl border border-[#E2E8F0] bg-white px-4 py-2.5 text-sm font-medium text-[#0F172A] hover:border-[#0F172A] transition-colors"
          >
            <RefreshCw size={15} className={loading ? "animate-spin" : ""} /> Refresh
          </button>
        </div>

        {error && <p className="text-red-600">{error}</p>}
        {loading ? (
          <p className="text-[#475569]">Loading...</p>
        ) : items.length === 0 ? (
          <div className="rounded-2xl border border-[#E2E8F0] bg-white p-12 text-center text-[#475569]">
            No submissions yet.
          </div>
        ) : (
          <div className="space-y-4">
            {items.map((c) => (
              <div key={c.id} data-testid={`admin-contact-${c.id}`} className="rounded-2xl border border-[#E2E8F0] bg-white p-6 shadow-sm">
                <div className="flex flex-wrap items-start justify-between gap-3">
                  <div>
                    <h3 className="font-semibold font-display text-[#0F172A]">{c.name}</h3>
                    <div className="flex flex-wrap gap-x-5 gap-y-1 mt-2 text-sm text-[#475569]">
                      <span className="inline-flex items-center gap-1.5"><Mail size={14} className="text-[#10B981]" />{c.email}</span>
                      {c.phone && <span className="inline-flex items-center gap-1.5"><Phone size={14} className="text-[#10B981]" />{c.phone}</span>}
                      {c.website && <span className="inline-flex items-center gap-1.5"><Globe size={14} className="text-[#10B981]" />{c.website}</span>}
                    </div>
                  </div>
                  <span className="text-xs text-[#94A3B8]">{new Date(c.created_at).toLocaleString()}</span>
                </div>
                <div className="mt-2 flex flex-wrap gap-2">
                  {c.source && (
                    <span className={`inline-block text-xs font-medium rounded-full px-2.5 py-0.5 ${c.source === "philippines" ? "text-[#0F172A] bg-amber-100" : "text-[#0F172A] bg-sky-100"}`}>
                      {c.source === "philippines" ? "🇵🇭 Philippines" : "🇪🇺 International"}
                    </span>
                  )}
                  {c.selected_plan && (
                    <span className="inline-block text-xs font-medium text-[#0F172A] bg-[#10B981]/15 rounded-full px-2.5 py-0.5">
                      Plan: {c.selected_plan}
                    </span>
                  )}
                </div>
                {c.message && <p className="mt-4 text-[#1E293B] leading-relaxed border-t border-[#E2E8F0] pt-4">{c.message}</p>}
                {c.newsletter && <span className="mt-3 inline-block text-xs font-medium text-[#10B981] bg-[#10B981]/10 rounded-full px-2.5 py-0.5">Newsletter opt-in</span>}
              </div>
            ))}
          </div>
        )}
      </Container>
    </div>
  );
}
